import Immutable from 'immutable';
import { isValidPhoneNumber } from 'react-phone-number-input';
import difflib from 'difflib';

import { checkIfUserIsEligibleToCompetition } from '../../containers/Competitions/CompetitionsHelper';
import { StringTMap, ValidationOptions } from '../../types';

import { COMMON_PASSWORDS } from './common-passwords';
import { FieldsHaveWithDependencies } from '../../reducers/AccountReducer/Helpers';
import { IntlShape } from 'react-intl';
import { formatMessageHelper } from './internationalization';

export const alphabeticalPlusRegex = /^[a-z\-.,\s]+$/i;

export const alphanumericPlusRegex = /^[a-zA-Z0-9\-.,\s]+$/i;
interface ValidFieldParams {
  field: string;
  value: any;
  state: Immutable.Collection<string, any>;
  validationOptions: ValidationOptions;
  options?: any;
}

export class UserDataFieldsValidation {
  employerFinanceFields: Immutable.List<string>;
  employerFields: Immutable.List<string>;

  fieldsWithDependencies = FieldsHaveWithDependencies;

  constructor() {
    this.employerFinanceFields = Immutable.List([
      'investmentAdvisory',
      'brokerage',
      'fundamentalResearch',
      'proprietaryTrading',
      'serviceEngagement',
      'employerServices',
      'exposureStatus',
      'exposureDetails',
    ]);

    this.employerFields = Immutable.List([
      'employer',
      'business',
      'title',
      'description',
      'nonPublicInformation',
      'participationPermitted',
      'finance',
    ]);
  }

  getRules({ isConsultant = false } = {}) {
    return {
      email: ['emailBlur'],
      referrer: ['referrer'],
      referrerToggle: [],
      referrerWizard: ['referrer'],
      changeEmail: ['required', 'email', 'matchCurrentEmail'],
      changeEmailPassword: ['requiredWithWhiteSpaces'],
      accessTokenPassword: ['requiredWithWhiteSpaces'],
      workdayPassword: ['requiredWithWhiteSpaces'],
      brainLabsPassword: ['requiredWithWhiteSpaces'],
      firstName: ['required', 'alphabeticValidation'],
      lastName: ['required', 'alphabeticValidation'],
      fullName: ['required', 'alphabeticValidation'],
      country: ['required'],
      countryWizard: ['required', 'checkCompetitionCountry'],
      countryPhoneMismatch: ['isTrue'],
      gender: ['required'],
      university: ['required'],
      universityWizard: ['required', 'checkCompetitionUniversity'],
      allowSMS: ['boolRequired'],
      telephone: ['requiredTelephone'],
      password: [
        'requiredWithWhiteSpaces',
        'passwordMinimumLength',
        'passwordNotNumeric',
        'passwordNotSimilarUserAttributes',
        'passwordNotCommon',
      ],
      confirmation: ['matchPassword', 'requiredWithWhiteSpaces'],
      maxGPA: ['requiredmaxGPA', 'greaterthanGPA'],
      captcha: ['required'],
      agree: ['isTrue'],
      agreePrivacyPolicy: ['isTrue'],
      agreeCompetition: ['checkCompetitionAgreement'],
      presently_employed: ['boolRequired'],
      employer: ['required'],
      business: ['required'],
      title: ['required'],
      description: ['required'],
      nonPublicInformation: ['boolRequired'],
      participationPermitted: ['boolRequired'],
      finance: ['boolRequired'],
      investmentAdvisory: ['boolRequired'],
      brokerage: ['boolRequired'],
      fundamentalResearch: ['boolRequired'],
      proprietaryTrading: ['boolRequired'],
      serviceEngagement: ['boolRequired'],
      employerServices: ['employerServices'],
      exposureStatus: ['boolRequired'],
      exposureDetails: ['exposureDetails'],
      state: ['alphabeticValidation'],
      city: ['alphabeticValidation'],
      // @ts-ignore
      major: ['alphabeticValidation'],
      street: ['addressValidation'],
      postalCode: ['addressValidation'],
      publicPhoto: ['required'],
      publicName: ['required'],
      'terms-conditions': ['isTrue'],
      ...(isConsultant && {
        degree: ['required'],
        graduationYear: ['required'],
        major: ['required', 'alphabeticValidation'],
      }),
    };
  }

  createValidation(state: any, fieldsToValidation: Immutable.List<string>) {
    let isValid = this.validation(state, fieldsToValidation);

    if (isValid && fieldsToValidation.includes('presently_employed') && state.getIn(['presently_employed', 'value']) === true) {
      isValid = this.validation(state, this.employerFields);
      if (isValid && state.getIn(['finance', 'value']) === true) {
        isValid = this.validation(state, this.employerFinanceFields);
      }
    }

    return isValid;
  }

  validation(state: any, fields: Immutable.List<string>): boolean {
    let isValid: boolean = true;
    if (fields !== undefined) {
      fields.forEach((field: string) => {
        if (state.getIn([field, 'valid']) === false) {
          isValid = false;
        }
      });
    } else {
      isValid = false;
    }

    return isValid;
  }

  validField({ field, value, state, validationOptions, options }: ValidFieldParams) {
    const rules: StringTMap<string[]> = this.getRules({ isConsultant: validationOptions.isConsultant || options?.get?.('isConsultant') });
    let errors: string[] = [];
    if (rules.hasOwnProperty(field)) {
      rules[field].forEach((rule: any) => {
        const fn: any = (this as any)[rule];
        if (fn && typeof fn === 'function') {
          errors = errors.concat(fn(value, state, options, field));
        }
      });

      const valid: boolean = errors.length === 0;

      return { errors, valid };
    } else {
      return { errors, valid: true };
    }
  }

  validBlurEmail(value: any) {
    let errors: string[] = [];
    errors = errors.concat(this.emailBlur(value));

    return { errors, valid: errors.length === 0 };
  }

  exposureDetails(value: any, state: any) {
    if ((value === null || value === undefined) && state.getIn(['exposureStatus', 'value']) === true) {
      return 'field.required';
    } else if (value !== null && value !== undefined && value.trim().length === 0 && state.getIn(['exposureStatus', 'value']) === true) {
      return 'field.required';
    } else {
      return [];
    }
  }

  employerServices(value: any, state: any) {
    if ((value === null || value === undefined) && state.getIn(['serviceEngagement', 'value']) === true) {
      return 'field.required';
    } else if (value !== null && value !== undefined && value.trim().length === 0 && state.getIn(['serviceEngagement', 'value']) === true) {
      return 'field.required';
    } else {
      return [];
    }
  }

  required(value: string, __: any) {
    if (value?.trim().length === 0) {
      return 'field.required';
    } else {
      return [];
    }
  }

  alphabeticValidation(value: string, __: any) {
    if (value.trim().length > 0 && !alphabeticalPlusRegex.test(value.trim())) {
      return 'field.alphabetic.error';
    } else {
      return [];
    }
  }

  addressValidation(value: string, __: any) {
    const regex = /^[0-9a-z\(\)\-\.\,\'\&\s]+$/i;
    if (value.trim().length > 0 && !regex.test(value.trim())) {
      return 'field.addressValidation.error';
    } else {
      return [];
    }
  }

  requiredWithWhiteSpaces(value: string, __: any) {
    if (value?.length === 0) {
      return 'field.required';
    } else {
      return [];
    }
  }

  boolRequired(value: string, __: any) {
    if (typeof value !== 'boolean') {
      return 'field.required';
    } else {
      return [];
    }
  }

  isTrue(value: string, __: any) {
    if (typeof value !== 'boolean' || value !== true) {
      return 'field.required';
    } else {
      return [];
    }
  }

  matchPassword(value: string, state: any) {
    if (value !== state.getIn(['password', 'value'])) {
      return 'field.match.password.failure';
    } else {
      return [];
    }
  }

  requiredmaxGPA(value: string, state: any) {
    if (value && value.toString().trim().length === 0 && state.getIn(['gpa', 'value']) !== '') {
      return 'field.required';
    } else {
      return [];
    }
  }

  greaterthanGPA(maxGpaValueString: string, state: any) {
    const gpaValueString = state.getIn(['gpa', 'value']);
    const maxGpa = parseFloat(maxGpaValueString);
    const gpaValue = parseFloat(gpaValueString);
    if (maxGpa < gpaValue) {
      return 'field.maxgpa.greater.gpa';
    } else {
      return [];
    }
  }

  requiredTelephone(value: string, _: any) {
    if (value === undefined) {
      return 'field.phone.required';
    } else if (value.trim().length === 0) {
      return 'field.phone.required';
    } else if (!isValidPhoneNumber(value)) {
      return 'field.phone.wrong.number';
    } else {
      return [];
    }
  }

  matchCurrentEmail(value: string, state: any) {
    if (state.getIn(['email', 'value']) === value) {
      return 'field.match.current.email';
    } else {
      return [];
    }
  }

  checkCompetitionAgreement(_: string, state: any, options: any) {
    const competition = options && options.get('competition');
    const countriesOptions = options && options.get('countriesOptions');
    const currentCountry = state.getIn(['country', 'value']);
    const currentUniversity = state.getIn(['university', 'value']);

    const result = checkIfUserIsEligibleToCompetition({ competition, countriesOptions, currentCountry, currentUniversity });
    return result;
  }

  public checkCompetitionCountry(value: any, formValues: any, options: any, intl: IntlShape) {
    if (!options.get('logoutCompetition')) {
      return [];
    }

    // While we allow some countries to early register IQC, we don't want to show that IQC only available in specific countries
    // Instead, we'll tell them to revisit when the IQC is open officially at March 18
    // TODO(huymnguyen): Remove on the registration date
    const competitionId = options?.getIn(['competition', 'id']);
    const isIQCCompetition = competitionId?.startsWith('IQC');

    const error = this.checkCompetitionAgreement(value, formValues, options)?.[0];
    return error?.values?.errorMessageCountries
      ? isIQCCompetition
        ? formatMessageHelper(intl, 'invalid.competition.country.IQC')
        : formatMessageHelper(intl, 'invalid.competition.country', error.values)
      : [];
  }

  public checkCompetitionUniversity(value: any, formValues: any, options: any, intl: IntlShape) {
    if (!options.get('logoutCompetition')) {
      return [];
    }

    const error = this.checkCompetitionAgreement(value, formValues, options)?.[0];

    return error?.values?.errorMessageUniversities ? formatMessageHelper(intl, 'invalid.competition.university', error.values) : [];
  }

  checkCompetitionAgreementWizard(value: any, formValues: any, options: any, intl: IntlShape) {
    const error = this.checkCompetitionAgreement(value, formValues, options)?.[0];
    return error ? formatMessageHelper(intl, error.id, error.values) : [];
  }

  email(value: string, state: any, _: any, field: string) {
    if (value === undefined) {
      return 'field.email.required';
    } else {
      const regex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;
      if (regex.test(value) === false && state.getIn([field, 'errors']) && state.getIn([field, 'errors']).toJS().length !== 0) {
        return 'field.email.invalid';
      } else {
        return [];
      }
    }
  }

  emailBlur(value: string) {
    const emptyField: any = [undefined, ''];
    if (emptyField.includes(value)) {
      return 'field.required';
    } else {
      const regex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;
      if (regex.test(value) === false) {
        return 'field.email.invalid';
      } else {
        return [];
      }
    }
  }

  referrer(value: string) {
    if (value.length === 0) {
      return [];
    } else if (!/^[A-Z]{2}\d{5}$/.test(value)) {
      return 'field.userid.invalid';
    } else {
      return [];
    }
  }

  // There is 4 validation rules for password on API:
  //   1. Check if password is longer than 8 chars (MinimumLengthValidator)
  //   2. Check if password contains only numerics (NumericPasswordValidator)
  //   3. Check if password is similar with first name, last name or email (UserAttributeSimilarityValidator)
  //   4. Check if password is common (CommonPasswordValidator)
  // https://gitlab.worldquant.com/wqbraintech/websim/-/blob/3552d36c049966810fcf538bbcd627b9d45e0002/appserver/websimdj/settings.py#L401

  passwordMinimumLength(value: string) {
    return value.length < 8 ? 'field.password.minimumLength' : [];
  }

  passwordNotNumeric(value: string) {
    return /^\d+$/.test(value) ? 'field.password.onlyNumeric' : [];
  }

  passwordNotSimilarUserAttributes(value: string, state: any) {
    const userAttributes = ['firstName', 'lastName', 'email'];

    for (const attribute of userAttributes) {
      const error = UserDataFieldsValidation.getPasswordSimilarityError(
        attribute,
        state,
        `field.password.similarUserAttributes.${attribute}`
      );
      if (error) {
        return error;
      }
    }

    return [];
  }

  firstNameNotSimilarToPassword(value: string, state: any) {
    const attribute = 'firstName';
    const error = UserDataFieldsValidation.getPasswordSimilarityError(attribute, state, `field.${attribute}.similarToPassword`);

    return error || [];
  }

  lastNameNotSimilarToPassword(value: string, state: any) {
    const attribute = 'lastName';

    const error = UserDataFieldsValidation.getPasswordSimilarityError(attribute, state, `field.${attribute}.similarToPassword`);

    return error || [];
  }

  // Follow implementation of `UserAttributeSimilarityValidator` of Django
  // https://github.com/django/django/blob/22b0b73c7732ba67db4e69fd9fa75aad84c8e5c4/django/contrib/auth/password_validation.py#L159
  static getPasswordSimilarityError(attribute: string, state: any, error: string) {
    const maxSimilarity = 0.7;
    const passwordLowerCase = state.getIn(['password', 'value']).toLowerCase();

    // https://github.com/django/django/blob/22b0b73c7732ba67db4e69fd9fa75aad84c8e5c4/django/contrib/auth/password_validation.py#L129
    function isExceedsMaximumLengthRatio(password: string, maxSimilarity: number, value: string) {
      const valueLength = value.length;
      const passwordLength = password.length;
      const lengthBoundSimilarity = (maxSimilarity / 2) * passwordLength;
      return passwordLength >= 10 * valueLength && valueLength < lengthBoundSimilarity;
    }

    const value = state.getIn([attribute, 'value']);

    if (!value || typeof value !== 'string' || value === '') {
      return undefined;
    }

    const valueLowerCase = value.toLowerCase();
    const valueParts = valueLowerCase.split(/\W+/).concat(valueLowerCase);
    for (const valuePart of valueParts) {
      if (isExceedsMaximumLengthRatio(passwordLowerCase, maxSimilarity, valuePart)) {
        continue;
      }

      const sm = new difflib.SequenceMatcher(null, passwordLowerCase, valuePart);
      if (sm.quickRatio() >= maxSimilarity) {
        return error;
      }
    }
  }

  passwordNotCommon(value: string) {
    return COMMON_PASSWORDS.has(value.toLowerCase()) ? 'field.password.commonPassword' : [];
  }
}
export const AccountDataFieldsValidation = UserDataFieldsValidation;
